// src/components/settings/UserProfileSettings.jsx
// Form input nama user di halaman Settings

import React, { useState, useEffect } from 'react';
import { User, Save, Trash2, Check, Edit2 } from 'lucide-react';

export default function UserProfileSettings({ profile, onSave, onClear }) {
  const [name, setName]             = useState(profile?.name || '');
  const [isEditing, setIsEditing]   = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // ── Sync state dengan prop saat profile berubah ───
  useEffect(() => {
    setName(profile?.name || '');
  }, [profile?.name]);

  // ── Cek apakah nama berbeda dari yang tersimpan ──
  const hasChanges = (name.trim() || 'Santri') !== (profile?.name || 'Santri');
  const isDefault  = !profile?.name || profile.name === 'Santri';

  // ── Handler Simpan ───────────────────────────────
  const handleSave = () => {
    const success = onSave(name);
    if (success) {
      setIsEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    }
  };

  // ── Handler Reset ────────────────────────────────
  const handleClear = () => {
    if (window.confirm('Reset nama ke default "Santri"?')) {
      onClear();
      setName('Santri');
      setIsEditing(false);
    }
  };

  // ── Dapatkan inisial untuk avatar ────────────────
  const getInitials = (str) => {
    if (!str || str === 'Santri') return 'س';
    return str.trim().charAt(0).toUpperCase();
  };

  return (
    <div
      className="
        bg-white dark:bg-slate-800
        rounded-3xl shadow-soft
        border border-stone-100 dark:border-slate-700
        overflow-hidden
      "
    >
      {/* ── Header ──────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-stone-100 dark:border-slate-700">
        <p className="text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest flex items-center gap-2">
          <User className="w-3.5 h-3.5" />
          Profil Pengguna
        </p>
      </div>

      <div className="p-4 space-y-4">
        {/* ── Avatar + Info Singkat ──────────────────── */}
        <div className="flex items-center gap-3">
          {/* Avatar dengan inisial */}
          <div
            className="
              w-14 h-14 rounded-3xl flex-shrink-0
              bg-gradient-to-br from-green-400 to-green-600
              flex items-center justify-center
              shadow-md shadow-green-500/20
            "
          >
            <span
              className="text-2xl font-extrabold text-white"
              style={{ fontFamily: 'Amiri, serif' }}
            >
              {getInitials(profile?.name)}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-extrabold text-slate-800 dark:text-slate-100 truncate">
              {profile?.name || 'Santri'}
            </p>
            <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
              {isDefault
                ? 'Belum ada nama yang diset'
                : 'Nama akan muncul di greeting header'}
            </p>
          </div>
        </div>

        {/* ── Form Input Nama ────────────────────────── */}
        <div className="space-y-2">
          <label
            htmlFor="user-name"
            className="block text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest"
          >
            Nama Panggilan
          </label>

          <div className="relative">
            <input
              id="user-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setIsEditing(true)}
              placeholder="Contoh: Ahmad Fauzi"
              maxLength={30}
              className="
                w-full px-4 py-3 pr-12
                bg-stone-50 dark:bg-slate-700
                border-2 border-stone-200 dark:border-slate-600
                rounded-2xl
                text-sm font-bold text-slate-800 dark:text-slate-100
                placeholder:text-stone-400 dark:placeholder:text-slate-500
                focus:outline-none focus:border-green-400
                focus:ring-2 focus:ring-green-400/20
                transition-all
              "
            />

            {/* Indikator Focus/Edit */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isEditing
                ? <Edit2 className="w-4 h-4 text-green-500" />
                : <User  className="w-4 h-4 text-stone-400" />
              }
            </div>
          </div>

          {/* Helper Text */}
          <p className="text-[10px] text-slate-400 dark:text-slate-500 leading-relaxed pl-1">
            Maksimal 30 karakter. Nama akan muncul di header dengan salam personal.
          </p>
        </div>

        {/* ── Tombol Aksi ────────────────────────────── */}
        <div className="flex gap-2 pt-1">
          {/* Tombol Simpan */}
          <button
            onClick={handleSave}
            disabled={!hasChanges || !name.trim()}
            className="
              flex-1
              flex items-center justify-center gap-1.5
              bg-gradient-to-r from-green-500 to-green-600
              hover:from-green-600 hover:to-green-700
              text-white text-xs font-extrabold
              py-3 rounded-2xl
              shadow-soft transition-all active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed
              disabled:active:scale-100
            "
          >
            {savedFlash
              ? <>
                  <Check className="w-4 h-4" />
                  Tersimpan!
                </>
              : <>
                  <Save className="w-4 h-4" />
                  Simpan Nama
                </>
            }
          </button>

          {/* Tombol Reset (hanya jika bukan default) */}
          {!isDefault && (
            <button
              onClick={handleClear}
              className="
                bg-red-50 dark:bg-red-900/20
                hover:bg-red-100 dark:hover:bg-red-900/40
                text-red-600 dark:text-red-400
                border border-red-200 dark:border-red-800/40
                text-xs font-bold
                px-3 py-3 rounded-2xl
                transition-all active:scale-95
              "
              title="Reset ke default"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* ── Preview Salam ──────────────────────────── */}
        <div className="bg-stone-50 dark:bg-slate-700/50 border border-stone-100 dark:border-slate-600 rounded-2xl p-3">
          <p className="text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
            Preview Header
          </p>
          <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-600 rounded-2xl p-3">
            <p className="text-xs font-extrabold text-slate-800 dark:text-slate-100">
              Hai, {name.trim() || 'Santri'}!
            </p>
            <p
              dir="rtl"
              className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5"
              style={{ fontFamily: 'Amiri, serif' }}
            >
              السَّلَامُ عَلَيْكُمْ
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}